import TextField from "@mui/material/TextField";
import {
  Button,
  Checkbox,
  Fade,
  FormControlLabel,
  InputAdornment,
  Paper,
  Popper,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import { FixedSizeList as List } from "react-window";

import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CopyAll as CopyIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from "@mui/icons-material";

import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  setPage,
  setSelectedMultiplePackages,
  setSelectedPackage,
  setSelectedPackageBrand,
  setSelectedPackageInterface,
  setSelectedPackageStatus,
  setSelectedProcessName,
} from "../../../../../features/monitoringPackagesSlice";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMonitoringPackages } from "../../../../../customHooks/useMonitoringPackages";
import { normalizeFilters } from "../packageHelper";

interface FilterOption {
  id: string;
  label: string;
}

const VirtualListItem = ({ index, style, data }: any) => {
  const { items, selectedItems, onToggle } = data;
  const item = items[index];

  return (
    <div style={style}>
      <FormControlLabel
        control={
          <Checkbox
            checked={selectedItems.some(
              (selectedId: string) => selectedId === String(item.id)
            )}
            onChange={() => onToggle(item.id)}
            color="secondary"
            size="small"
          />
        }
        label={item.label}
        sx={{
          width: "100%",
          margin: 0,
          padding: "4px 8px",
          "& .MuiFormControlLabel-label": {
            fontSize: "0.875rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "200px",
          },
        }}
      />
    </div>
  );
};

const PackagesFilterCard = () => {
  const dispatch = useDispatch();
  const popperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<any>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    packagesFilters,
    // packageList,
    selectedPackage,
    selectedMultiplePackages,
   selectedProcessName,
    selectedPackageStatus,
    selectedPackageBrand,
    selectedPackageInterface,
    selectedPackageDateRange,
  } = useSelector((state: any) => state.monitoringPackages);

  const { fetchMonitoringPackages } = useMonitoringPackages();

  // UI states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentFilter, setCurrentFilter] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>(
    {}
  ) as any;

  // Add click-away listener
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popperRef.current &&
        !popperRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        setAnchorEl(null);
      }
    };

    if (anchorEl) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [anchorEl]);

  useEffect(() => {
    setFilterOptions((prev) => ({
      package: {
        ...prev.package,
        data: Array.from(
          new Map(
            (packagesFilters?.PACKAGE || []).map((item: any) => [
              item.id || item.value,
              { id: item.id || item.value, label: item.label },
            ])
          ).values()
        ),
      },
      processName: {
        ...prev.processName,
        data:
          packagesFilters?.PROCESS_NAME?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [],
      },
      brand: {
        ...prev.brand,
        data:
          packagesFilters?.BRAND?.map((item: any) => ({
            id: item.id || item.value,
            label: `${item.label} (${item.id || item.value})`,
          })) || [],
      },
      interface: {
        ...prev.interface,
        data:
          packagesFilters?.INTERFACE?.map((item: any) => ({
            id: item.id || item.value,
            label: `${item.label} (${item.id || item.value})`,
          })) || [],
      },
      status: {
        ...prev.status,
        data:
          packagesFilters?.STATUS?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [],
      },

      singlePackage: {
        ...prev.singlePackage,
        data:
          packagesFilters?.PACKAGE?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [],
      },
    }));
  }, [packagesFilters]);

  useEffect(() => {
    const initializeFiltersFromURL = () => {
      const updatedOptions = { ...filterOptions };

      // Update data options from packagesFilters (if available)
      if (packagesFilters) {
        updatedOptions.package.data = Array.from(
          new Map(
            (packagesFilters?.PACKAGE || []).map((item: any) => [
              item.id || item.value,
              { id: item.id || item.value, label: item.label },
            ])
          ).values()
        );

        updatedOptions.processName.data =
          packagesFilters?.PROCESS_NAME?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [];
        updatedOptions.brand.data =
          packagesFilters?.BRAND?.map((item: any) => ({
            id: item.id || item.value,
            label: `${item.label} (${item.id || item.value})`,
          })) || [];
        updatedOptions.interface.data =
          packagesFilters?.INTERFACE?.map((item: any) => ({
            id: item.id || item.value,
            label: `${item.label} (${item.id || item.value})`,
          })) || [];
        updatedOptions.status.data =
          packagesFilters?.STATUS?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [];

        updatedOptions.singlePackage.data =
          packagesFilters?.PACKAGE?.map((item: any) => ({
            id: item.id || item.value,
            label: item.label,
          })) || [];
      }

      // Set selected values from URL params (using IDs directly)
      Object.keys(updatedOptions).forEach((key) => {
        const filterKey =
          updatedOptions[key as keyof typeof updatedOptions].filterName;
        const paramValue = searchParams.get(filterKey);
        if (paramValue) {
          const ids = paramValue.split(",");
          updatedOptions[key as keyof typeof updatedOptions].selected = ids;
          // Dispatch the action to update Redux state
          updatedOptions[key as keyof typeof updatedOptions].action(ids);
        }
      });

      setFilterOptions(updatedOptions);

      // Set active filters based on URL params (using IDs directly)
      const activeFiltersFromURL: Record<string, string[]> = {};
      Object.keys(updatedOptions).forEach((key) => {
        const filterKey =
          updatedOptions[key as keyof typeof updatedOptions].filterName;
        const paramValue = searchParams.get(filterKey);
        if (paramValue) {
          activeFiltersFromURL[filterKey] = paramValue.split(",");
        }
      });
      setActiveFilters(activeFiltersFromURL);
    };

    initializeFiltersFromURL();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packagesFilters, searchParams]);
  // Filter options data
  const [filterOptions, setFilterOptions] = useState({
    package: {
      label: "Package",
      data:
        packagesFilters?.PACKAGE?.map((item: any) => ({
          id: item.id || item.value,
          label: item.label,
        })) || [],
      selected: selectedMultiplePackages || [],
      action: (value: any) => dispatch(setSelectedMultiplePackages(value)),
      searchTerm: "",
      filterName: "package_name",
    },
    processName: {
      label: "Process Name",
      data:
        packagesFilters?.PROCESS_NAME?.map((item: any) => ({
          id: item.id || item.value,
          label: item.label,
        })) || [],
      selected: selectedProcessName || [],
      action: (value: any) => dispatch(setSelectedProcessName(value)),
      searchTerm: "",
      filterName: "process_id",
    },
    status: {
      label: "Status",
      data:
        packagesFilters?.STATUS?.map((item: any) => ({
          id: item.id || item.value,
          label: item.label,
        })) || [],
      selected: selectedPackageStatus || [],
      action: (value: any) => dispatch(setSelectedPackageStatus(value)),
      searchTerm: "",
      filterName: "status",
    },
    brand: {
      label: "Brand",
      data:
        packagesFilters?.BRAND?.map((item: any) => ({
          id: String(item.id || item.value),
          label: `${item.label} (${item.id || item.value})`,
        })) || [],
      selected: selectedPackageBrand || [],
      action: (value: any) => dispatch(setSelectedPackageBrand(value)),
      searchTerm: "",
      filterName: "brand_id",
    },
    interface: {
      label: "Interface",
      data:
        packagesFilters?.INTERFACE?.map((item: any) => ({
          id: String(item.id || item.value),
          label: `${item.label} (${item.id || item.value})`,
        })) || [],
      selected: selectedPackageInterface || [],
      action: (value: any) => dispatch(setSelectedPackageInterface(value)),
      searchTerm: "",
      filterName: "interface_id",
    },

    singlePackage: {
      label: "Package",
      data:
        packagesFilters?.PACKAGE?.map((item: any) => ({
          id: item.id || item.value,
          label: item.label,
        })) || [],
      selected: selectedPackage || [],
      action: (value: any) => dispatch(setSelectedPackage(value)),
      searchTerm: "",
      filterName: "single_package_name",
    },
  });

  // State to track initial selected items when popup opens
  const [initialSelectedItems, setInitialSelectedItems] = useState<string[]>(
    []
  );

  // Memoized filtered and sorted data
  const filteredAndSortedData = useMemo(() => {
    if (!currentFilter) return [];

    const currentOptions =
      filterOptions[currentFilter as keyof typeof filterOptions];
    const filtered = currentOptions.data.filter((item: FilterOption) =>
      item.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort: initial selected items first (from when popup was opened), then alphabetically
    return [...filtered].sort((a: FilterOption, b: FilterOption) => {
      // Initial selected items come first
      const aInitiallySelected = initialSelectedItems.includes(String(a.id));
      const bInitiallySelected = initialSelectedItems.includes(String(b.id));

      if (aInitiallySelected && !bInitiallySelected) return -1;
      if (!aInitiallySelected && bInitiallySelected) return 1;

      // Then sort alphabetically
      return a.label.localeCompare(b.label);
    });
  }, [currentFilter, searchTerm, filterOptions, initialSelectedItems]);

  // Handle filter button click
  const handleFilterClick =
    (filterName: string) => (event: React.MouseEvent<HTMLButtonElement>) => {
      // If clicking the same filter button that's already open, close it
      if (anchorEl && currentFilter === filterName) {
        setAnchorEl(null);
        setCurrentFilter("");
        return;
      }

      setCurrentFilter(filterName);
      setAnchorEl(event.currentTarget);
      setSearchTerm("");

      // Store the current selected items as initial state when opening
      const currentOptions =
        filterOptions[filterName as keyof typeof filterOptions];
      setInitialSelectedItems([...currentOptions.selected]);

      // Reset scroll position
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.scrollToItem(0);
        }
      }, 100);
    };

  // Handle filter selection change
  const handleFilterChange = (id: string) => {
    const currentOptions =
      filterOptions[currentFilter as keyof typeof filterOptions];
    const filterName = currentOptions.filterName;

    // Calculate new selection for this filter (using IDs)
    const stringId = String(id);
    const newSelection = currentOptions.selected.includes(stringId)
      ? currentOptions.selected.filter((item: string) => item !== stringId)
      : [...currentOptions.selected, stringId];

    // Update filter options state
    setFilterOptions((prev) => ({
      ...prev,
      [currentFilter]: {
        ...prev[currentFilter as keyof typeof filterOptions],
        selected: newSelection,
      },
    }));

    // Dispatch the Redux action (with IDs)
    currentOptions.action(newSelection);

    // Update URL params (store IDs directly)
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set("page", "0");
      if (newSelection.length > 0) {
        newParams.set(filterName, newSelection.join(","));
      } else {
        newParams.delete(filterName);
      }
      return newParams;
    });

    // Update active filters with IDs (for API calls)
    const updatedFilters = {
      ...activeFilters,
      [filterName]: newSelection.length > 0 ? newSelection : undefined,
    };

    // Clean up undefined filters
    const cleanedFilters = Object.fromEntries(
      Object.entries(updatedFilters).filter(([_, v]) => v !== undefined)
    );

    setActiveFilters(cleanedFilters);

    // Make API call with ALL active filters (using IDs)
    const from_date = selectedPackageDateRange[0].format("YYYY-MM-DD");
    const to_date = selectedPackageDateRange[1].format("YYYY-MM-DD");

    fetchMonitoringPackages(
      from_date,
      to_date,
      1000,
      0,
      undefined,
      normalizeFilters(cleanedFilters)
    );
  };

  // Refresh data
  const handleRefresh = () => {
    const from_date = selectedPackageDateRange[0]?.format("YYYY-MM-DD");
    const to_date = selectedPackageDateRange[1]?.format("YYYY-MM-DD");

    fetchMonitoringPackages(
      from_date,
      to_date,
      1000,
      0,
      undefined,
      normalizeFilters(activeFilters)
    );

    dispatch(setPage(0));
    setSearchParams((prev) => {
      const updatedParams = new URLSearchParams(prev);
      updatedParams.set("page", "0");
      return updatedParams;
    });

    setSnackbarMessage("Data refreshed");
    setSnackbarOpen(true);
  };

  // Copy URL to clipboard
  const handleCopyURL = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setSnackbarMessage("URL copied to clipboard!");
      setSnackbarOpen(true);
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    // Reset state
    const from_date = selectedPackageDateRange[0].format("YYYY-MM-DD");
    const to_date = selectedPackageDateRange[1].format("YYYY-MM-DD");

    fetchMonitoringPackages(from_date, to_date, 1000, 0);
    Object.values(filterOptions).forEach((option) => {
      option.action([]); // Dispatch action with empty array to reset
    });
    setFilterOptions((prev) => {
      const resetOptions = { ...prev };
      Object.keys(resetOptions).forEach((key) => {
        resetOptions[key as keyof typeof resetOptions].selected = [];
      });
      return resetOptions;
    });
    setActiveFilters({});

    // Reset URL
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      Object.keys(filterOptions).forEach((key) =>
        newParams.delete(
          filterOptions[key as keyof typeof filterOptions].filterName
        )
      );
      newParams.delete("search");
      newParams.set("page", "0");
      return newParams;
    });

    setSnackbarMessage("All filters cleared");
    setSnackbarOpen(true);
  };

  // Check if any filter is applied
  const isFilterApplied = Object.values(filterOptions).some(
    (option) => option.selected.length > 0
  );

  return (
    <>
      <Stack
        direction="row"
        spacing={2}
        justifyContent={"space-between"}
        alignItems={"center"}
        sx={{ width: "100%" }}
      >
        <Stack direction="row" gap={2} alignItems={"center"}>
          {/* Filter Buttons */}
          {Object.entries(filterOptions)
            .filter(([key]) => key !== "singlePackage" && key !== "package") // Exclude singlePackage and package filters from buttons
            .map(([key, option]) => (
              <Button
                key={key}
                sx={{
                  "&:hover": { backgroundColor: "rgba(156, 39, 176, 0.04)" },
                  textTransform: "capitalize",
                  minWidth: "auto",
                }}
                onClick={handleFilterClick(key)}
              >
                <span
                  style={{
                    color: option.selected.length > 0 ? "#9C27B0" : "#000000DE",
                    marginRight: option.selected.length > 0 ? "4px" : "0",
                  }}
                >
                  {option.label}
                </span>
                {option.selected.length > 0 ? (
                  <span style={{ color: "#9C27B0" }}>
                    +{option.selected.length}
                  </span>
                ) : (
                  <ArrowDownIcon
                    style={{
                      color:
                        anchorEl && currentFilter === key
                          ? "#9C2780"
                          : "#000000DE",
                      fontSize: "1rem",
                    }}
                  />
                )}
              </Button>
            ))}

          {/* Clear Filter Button */}
          <Button
            sx={{
              textTransform: "capitalize",
              fontWeight: 500,
              color: "#000000DE",
              "&:hover": {
                color: "#9C27B0",
                backgroundColor: "rgba(156, 39, 176, 0.04)",
              },
            }}
            onClick={clearAllFilters}
            disabled={!isFilterApplied}
          >
            Clear Filter
          </Button>
          {/* <PackagesFilterItem clearSelectedPackage={clearSelectedPackage} /> */}
        </Stack>
        {/* Right side - Actions */}
        <Stack
          direction="row"
          gap={2}
          justifyContent={"flex-end"}
          paddingRight={1}
        >
          <Tooltip title="Copy Link">
            <CopyIcon
              sx={{ cursor: "pointer" }}
              color="secondary"
              onClick={handleCopyURL}
            />
          </Tooltip>
          <Tooltip title="Refresh">
            <RefreshIcon
              color="secondary"
              onClick={handleRefresh}
              sx={{ cursor: "pointer" }}
            />
          </Tooltip>
        </Stack>
      </Stack>
      {/* Enhanced Filter Popup with Virtual Scrolling */}
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        transition
        sx={{ zIndex: 1200 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper
              ref={popperRef}
              sx={{
                width: 300,
                maxHeight: 400,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {currentFilter && (
                <>
                  {/* Fixed Header */}
                  <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
                    <Typography
                      variant="body2"
                      sx={{ fontSize: 16, fontWeight: 500, mb: 1 }}
                    >
                      Select{" "}
                      {
                        filterOptions[
                          currentFilter as keyof typeof filterOptions
                        ].label
                      }
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      color="secondary"
                    />
                  </Box>

                  {/* Virtualized List */}
                  <Box sx={{ flex: 1, minHeight: 0 }}>
                    {filteredAndSortedData.length > 0 ? (
                      // In the Popper component's List render:
                      <List
                        ref={listRef}
                        width={300}
                        height={Math.min(
                          300,
                          filteredAndSortedData.length * 40
                        )}
                        itemCount={filteredAndSortedData.length}
                        itemSize={40}
                        itemData={{
                          items: filteredAndSortedData,
                          selectedItems:
                            filterOptions[
                              currentFilter as keyof typeof filterOptions
                            ].selected,
                          onToggle: handleFilterChange,
                        }}
                      >
                        {VirtualListItem}
                      </List>
                    ) : (
                      <Box
                        sx={{
                          p: 2,
                          textAlign: "center",
                          color: "text.secondary",
                        }}
                      >
                        No options found
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Paper>
          </Fade>
        )}
      </Popper>
      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
};

export default PackagesFilterCard;
